import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Play, Coffee, ArrowClockwise, Stop, Clock } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";

const TRACKED = new Set(["office", "office_staff", "manager", "hr"]);

function fmt(ms) {
  if (!ms || ms < 0) return "0h 0m";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${h}h ${m}m`;
}

export default function DutyWidget({ user }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const tracked = user && TRACKED.has(user.role);
  const hbRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/my-duty/current");
      setS(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!tracked) return;
    load();
    // 30s heartbeat while on duty
    hbRef.current = setInterval(async () => {
      try {
        await api.post("/my-duty/activity");
        load();
      } catch { /* ignore */ }
    }, 30000);
    return () => { if (hbRef.current) clearInterval(hbRef.current); };
    // eslint-disable-next-line
  }, [tracked]);

  if (!tracked) return null;

  const act = async (path, success) => {
    setBusy(true);
    try {
      await api.post(path);
      toast.success(success);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const onBreak = s?.status === "ON_BREAK";
  const active = s?.status === "ON_DUTY_ACTIVE";
  const offDuty = !s;

  return (
    <div data-testid="duty-widget"
         className={`flex items-center gap-2 px-3 h-10 rounded-md border ${active ? "bg-[#2D5A27] text-white border-[#2D5A27]" : onBreak ? "bg-[#C89E3E] text-white border-[#C89E3E]" : "bg-white border-[#E5E2D9]"}`}>
      <Clock size={14} weight={active || onBreak ? "fill" : "regular"}/>
      {offDuty ? (
        <>
          <span className="text-xs">Off duty</span>
          <button onClick={() => act("/my-duty/start", "Duty started")} disabled={busy}
                  data-testid="duty-widget-start"
                  className="ml-1 inline-flex items-center gap-1 h-7 px-2 text-xs bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded">
            <Play size={10} weight="fill"/>Start
          </button>
        </>
      ) : (
        <>
          <span className="font-mono text-xs">
            {fmt(s.active_ms)}{s.break_ms ? ` · ☕ ${fmt(s.break_ms)}` : ""}
          </span>
          {active && (
            <>
              <button onClick={() => act("/my-duty/break", "On break")} disabled={busy}
                      data-testid="duty-widget-break"
                      className="inline-flex items-center gap-1 h-7 px-2 text-xs bg-white/20 hover:bg-white/30 rounded">
                <Coffee size={10}/>Break
              </button>
              <button onClick={() => act("/my-duty/end", "Duty ended")} disabled={busy}
                      data-testid="duty-widget-end"
                      className="inline-flex items-center gap-1 h-7 px-2 text-xs bg-white/20 hover:bg-white/30 rounded">
                <Stop size={10} weight="fill"/>End
              </button>
            </>
          )}
          {onBreak && (
            <button onClick={() => act("/my-duty/resume", "Back to duty")} disabled={busy}
                    data-testid="duty-widget-resume"
                    className="inline-flex items-center gap-1 h-7 px-2 text-xs bg-white/30 hover:bg-white/40 rounded">
              <ArrowClockwise size={10}/>Resume
            </button>
          )}
        </>
      )}
    </div>
  );
}
